import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonWidth,
  IconChat,
  IconStarOutline,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import React, { type FC, useCallback, useState } from 'react';

import {
  CoachVisibility,
  type GoalName,
  MatchedCoachStatus,
  type Scalars,
} from '../../__generated-gql-types__/globalTypes';
import mxpnl from '../../utils/analytics/mixpanel';
import phog from '../../utils/analytics/posthog';
import { isCoachAvailabilityOnboarded } from '../../utils/availability';
import { categoryUrnToSlug } from '../../utils/category';
import { getCTATooltipTextForBlockedUser } from '../../utils/coach';
import { getUrlObject } from '../../utils/url';
import { useLoginModal } from '../LoginModal';
import LelandExperienceGuarantee from '../common/LelandExperienceGuarantee';
import { usePhoneNumberModal } from '../sms/PhoneNumberModal';

import { CoachAvailabilityInfo } from './CoachAvailabilityInfo';
import IntroVideo from './IntroVideo';
import { MessagingCallout } from './MessagingCallout';
import UpcomingSessionsCTA from './UpcomingSessionsCTA';
import {
  type CoachMessageCtaCoachFragment,
  CoachMessageCtaGetCoachByIdDocument,
  CoachMessageCtaGetUserDocument,
  type CoachMessageCtaUserFragment,
  useCoachMessageCtaGetCoachByIdQuery,
  useCoachMessageCtaGetUserQuery,
  useCoachMessageCtaLinkedInSelfServeRequestMutation,
} from './__generated-gql-types__/CoachMessageCTA.generated';
import { useMessageModal } from './modals/MessageModal';
import { useRequestCoachMatchModal } from './modals/RequestCoachMatchModal';
import { SessionOption, useScheduleModal } from './modals/ScheduleModal';

enum CTAType {
  MESSAGE,
  BOOK_COACH,
  LEAVE_A_REVIEW,
  SCHEDULE_SESSION,
  SCHEDULE_INTRO_CALL,
  REQUEST_COACH_MATCH,
}

interface CoachMessageCTAProps {
  user: Possible<CoachMessageCtaUserFragment>;
  coach: CoachMessageCtaCoachFragment;
  onReviewsClick: () => void;
  onBuyCoaching: () => void;
  className?: string;
  goal: Possible<GoalName>;
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  hasReviewed?: boolean;
  setIsReviewModalOpen: (open: boolean) => void;
  blocked?: boolean;
}

interface RequestMatchCTAProps {
  isRequestedMatch: boolean;
  handleClick: () => void;
  blocked: boolean | undefined;
  blockedUserCTAText: string;
  onTrialOrder?: boolean;
  coachFirstName?: string;
}

const RequestMatchCTA: FC<RequestMatchCTAProps> = ({
  isRequestedMatch,
  handleClick,
  blocked,
  blockedUserCTAText,
  onTrialOrder,
  coachFirstName,
}) => (
  <>
    {onTrialOrder && isRequestedMatch && coachFirstName ? (
      <div className="flex flex-col gap-2">
        <div className="h-1 w-full animate-progress rounded-full bg-subscriber-progress-bar bg-repeat-x">
          &nbsp;
        </div>
        <div>
          <h3 className="font-medium">
            You requested to work with this coach.
          </h3>
          <p className="text-leland-gray-light">
            The Leland team is reviewing {coachFirstName}&rsquo;s availability.
            Generally this process can take 2-3 days.
          </p>
        </div>
      </div>
    ) : (
      <>
        {onTrialOrder ? (
          <p className="text-leland-gray-light">
            Reqest to apply your free month to this coach by clicking below.
            Please note that availability with this coach isn&rsquo;t
            guaranteed.
          </p>
        ) : null}
        <Button
          label={
            isRequestedMatch ? 'Match requested' : 'Request to work together'
          }
          buttonColor={
            isRequestedMatch ? ButtonColor.WHITE : ButtonColor.PRIMARY
          }
          onClick={handleClick}
          width={ButtonWidth.FULL}
          disabled={blocked || isRequestedMatch}
          tooltipContent={blocked ? blockedUserCTAText : null}
          rounded
        />
      </>
    )}
  </>
);

const CoachMessageCTA: FC<CoachMessageCTAProps> = ({
  user,
  coach: coachProp,
  onBuyCoaching,
  className = '',
  categoryUrn,
  goal,
  hasReviewed,
  setIsReviewModalOpen,
  blocked,
}) => {
  const { data: clientUserData } = useCoachMessageCtaGetUserQuery();

  const currentUser = clientUserData?.user ?? user;

  const router = useRouter();
  const isMd = useMediaQuery(Breakpoints.MD);

  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();
  const { setOpen: setMessageModalOpen, Modal: MessageModal } =
    useMessageModal();
  const { setOpen: setScheduleModalOpen, Modal: ScheduleModal } =
    useScheduleModal();
  const {
    openModalIfNecessary: openPhoneNumberModalIfNecessary,
    Modal: PhoneNumberModal,
  } = usePhoneNumberModal();
  const {
    setOpen: setRequestCoachMatchModalOpen,
    Modal: RequestCoachMatchModal,
  } = useRequestCoachMatchModal();

  const [CTAClicked, setCTAClicked] = useState<CTAType | null>(null);

  const categorySlug = categoryUrn ? categoryUrnToSlug(categoryUrn) : null;

  const { data: hydratedCoachData, refetch: refetchCoachData } =
    useCoachMessageCtaGetCoachByIdQuery({
      variables: {
        id: coachProp.id,
        categoryUrn: categoryUrn,
      },
    });

  const coach = hydratedCoachData?.coach ?? coachProp;
  const isLoggedIn = !!currentUser?.applicant;
  const mostRecentOrder = coach?.mostRecentOrder;
  const coachingRelationshipExists = !!coach?.coachingRelationship;
  const couldScheduleIntro =
    (coach?.couldScheduleIntro ?? true) &&
    coach.visibility !== CoachVisibility.PRIVATE_CLOSED;
  const hasBookedWithCoach = !!mostRecentOrder;
  const hasReviewedCoach = !!(
    coach?.coachingRelationship?.review ?? hasReviewed
  );
  const blockedUserCTAText = getCTATooltipTextForBlockedUser(
    coach.user.firstName,
  );

  const isNewSubscriptionExperience =
    coach.newExperience ||
    currentUser?.applicant?.newExperience ||
    coach.coachingRelationship?.newExperience;

  const handleLeaveAReview = useCallback(() => {
    setCTAClicked(CTAType.LEAVE_A_REVIEW);
    if (isLoggedIn) {
      setIsReviewModalOpen(true);
    } else {
      setLoginModalOpen(true);
    }
  }, [isLoggedIn, setLoginModalOpen, setIsReviewModalOpen]);

  const showAvailabilityUI =
    coach?.availability &&
    isCoachAvailabilityOnboarded(coach.availability.defaultAvailability);

  const onTrialOrder = !!currentUser?.applicant?.hasUnassignedTrialOrder;

  const isRequestedMatch =
    coach.matchedCoach?.status === MatchedCoachStatus.REQUESTED;
  const isDeclinedMatch =
    coach.matchedCoach?.status === MatchedCoachStatus.DECLINED;

  const canPurchase =
    coach?.visibility !== CoachVisibility.PRIVATE_CLOSED ||
    !!coach.coachingRelationship;

  const handleScheduleSession = () => {
    if (isLoggedIn) {
      setScheduleModalOpen(true);
    } else {
      setLoginModalOpen(true);
    }
  };

  const messageCoach = async () => {
    if (coachingRelationshipExists) {
      await router.push(getUrlObject('/inbox', { coach: coach.id }));
    } else {
      mxpnl.track('clickedMessageCoach');
      phog.capture('coach_message_cta_message_coach_click');
      setMessageModalOpen(true);
    }
  };

  const onSuccessfulMessagedCoach = async () => {
    mxpnl.track('coachMessageCTA - messaged coach');
    phog.capture('coach_message_cta_message_coach_success');
    await refetchCoachData();
  };

  const handleMessageCoach = async () => {
    setCTAClicked(CTAType.MESSAGE);
    if (isLoggedIn) {
      await messageCoach();
    } else {
      setLoginModalOpen(true);
    }
  };

  const [requestMatchAction] =
    useCoachMessageCtaLinkedInSelfServeRequestMutation();

  const handleRequestLinkedinMatch = useCallback(async () => {
    if (!currentUser?.applicant || !categoryUrn) {
      return;
    }
    await requestMatchAction({
      variables: {
        input: {
          applicantUrn: currentUser?.applicant.id,
          coachUrn: coach.id,
          status: MatchedCoachStatus.REQUESTED,
          categoryUrn,
        },
      },
      optimisticResponse: {
        createOpportunity: {
          id: 'temp-id',
          status: MatchedCoachStatus.REQUESTED,
          coach: {
            id: coach.id,
            matchedCoach: {
              id: 'temp-id',
              status: MatchedCoachStatus.REQUESTED,
            },
          },
        },
      },
    });
  }, [categoryUrn, coach.id, currentUser?.applicant, requestMatchAction]);

  const handleRequestCoachMatch = useCallback(() => {
    setCTAClicked(CTAType.REQUEST_COACH_MATCH);
    if (isLoggedIn) {
      if (onTrialOrder) {
        void handleRequestLinkedinMatch();
      } else {
        setRequestCoachMatchModalOpen(true);
      }
    } else {
      setLoginModalOpen(true);
    }
  }, [
    handleRequestLinkedinMatch,
    isLoggedIn,
    onTrialOrder,
    setLoginModalOpen,
    setRequestCoachMatchModalOpen,
  ]);

  return (
    <div className={`flex flex-col space-y-4 ${className}`}>
      <div className="bg-white md:rounded-lg md:border md:shadow-sm">
        {coach.introVideo && isMd ? (
          <IntroVideo
            className="h-48"
            videoClassName="rounded-b-none"
            video={coach.introVideo}
            coach={coach}
            categorySlug={categorySlug}
          />
        ) : null}

        <div className="space-y-3 md:space-y-4 md:p-4">
          <CoachAvailabilityInfo
            className="hidden md:block"
            coach={coach}
            overrideShowClosedNoMessage={isDeclinedMatch}
          />
          <div className="mt-4 w-full space-y-3 md:mt-0 md:space-y-4">
            {isDeclinedMatch ? null : (isNewSubscriptionExperience &&
                isRequestedMatch) ||
              onTrialOrder ? (
              goal && categoryUrn ? (
                <RequestMatchCTA
                  isRequestedMatch={isRequestedMatch}
                  handleClick={handleRequestCoachMatch}
                  blocked={blocked}
                  blockedUserCTAText={blockedUserCTAText}
                  onTrialOrder={onTrialOrder}
                  coachFirstName={coach.user.firstName}
                />
              ) : null
            ) : showAvailabilityUI ? (
              <>
                {canPurchase ? (
                  <Button
                    buttonColor={ButtonColor.PRIMARY}
                    label={
                      couldScheduleIntro
                        ? 'Schedule a free intro call'
                        : 'Book a session'
                    }
                    width={ButtonWidth.FULL}
                    disabled={blocked}
                    tooltipContent={blocked ? blockedUserCTAText : null}
                    onClick={() => {
                      setCTAClicked(
                        couldScheduleIntro
                          ? CTAType.SCHEDULE_INTRO_CALL
                          : CTAType.SCHEDULE_SESSION,
                      );
                      handleScheduleSession();
                    }}
                    rounded
                  />
                ) : null}

                {couldScheduleIntro ? (
                  <Button
                    label="Book a session"
                    width={ButtonWidth.FULL}
                    disabled={blocked}
                    tooltipContent={blocked ? blockedUserCTAText : null}
                    onClick={() => {
                      setCTAClicked(CTAType.SCHEDULE_SESSION);
                      handleScheduleSession();
                    }}
                    rounded
                  />
                ) : coachingRelationshipExists ? (
                  <Button
                    label={`Message ${coach.user.firstName}`}
                    rounded
                    width={ButtonWidth.FULL}
                    disabled={blocked}
                    tooltipContent={blocked ? blockedUserCTAText : null}
                    onClick={handleMessageCoach}
                  />
                ) : null}

                <ScheduleModal
                  coach={coach}
                  categoryUrn={categoryUrn}
                  defaultSessionType={
                    CTAClicked === CTAType.SCHEDULE_INTRO_CALL
                      ? SessionOption.INTRO_CALL
                      : SessionOption.COACHING_SESSION
                  }
                  refetchQueries={[CoachMessageCtaGetCoachByIdDocument]}
                  onSuccessModalDismiss={openPhoneNumberModalIfNecessary}
                />
              </>
            ) : (
              <>
                <Button
                  label={
                    coachingRelationshipExists
                      ? `Message ${coach.user.firstName}`
                      : 'Get Started for Free'
                  }
                  LeftIcon={coachingRelationshipExists ? IconChat : undefined}
                  buttonColor={ButtonColor.PRIMARY}
                  width={ButtonWidth.FULL}
                  disabled={blocked}
                  tooltipContent={blocked ? blockedUserCTAText : null}
                  rounded
                  onClick={handleMessageCoach}
                />
                <Button
                  label="Buy coaching"
                  onClick={onBuyCoaching}
                  width={ButtonWidth.FULL}
                  disabled={blocked}
                  tooltipContent={blocked ? blockedUserCTAText : null}
                  rounded
                />
              </>
            )}

            {hasBookedWithCoach && !hasReviewedCoach ? (
              <Button
                label={isMd ? 'Leave a review' : 'Review'}
                LeftIcon={IconStarOutline}
                width={ButtonWidth.FULL}
                disabled={blocked}
                tooltipContent={blocked ? blockedUserCTAText : null}
                rounded
                onClick={handleLeaveAReview}
              />
            ) : null}
            {onTrialOrder && isRequestedMatch ? null : (
              <LelandExperienceGuarantee className="hidden justify-center md:flex" />
            )}
          </div>
          <CoachAvailabilityInfo
            className="md:hidden"
            coach={coach}
            overrideShowClosedNoMessage={isDeclinedMatch}
          />

          {/* MOBILE */}
          {coach.introVideo && !isMd ? (
            <IntroVideo
              className="relative mt-4 pb-50%"
              videoClassName="absolute left-0 top-0 h-full"
              video={coach.introVideo}
              coach={coach}
              categorySlug={categorySlug}
            />
          ) : null}
        </div>
      </div>

      {couldScheduleIntro && !blocked && !onTrialOrder ? (
        <MessagingCallout
          coachFirstName={coach.user.firstName}
          onMessage={handleMessageCoach}
        />
      ) : coach?.id ? (
        <UpcomingSessionsCTA
          className="mt-4"
          categoryUrn={categoryUrn}
          coachId={coach.id}
          blocked={blocked}
        />
      ) : null}

      <LoginModal
        onApplicantLogin={async () => {
          setLoginModalOpen(false);
          if (CTAClicked === CTAType.MESSAGE) {
            await messageCoach();
          } else if (CTAClicked === CTAType.BOOK_COACH) {
            onBuyCoaching();
          } else if (CTAClicked === CTAType.LEAVE_A_REVIEW) {
            setIsReviewModalOpen(true);
          } else if (CTAClicked === CTAType.SCHEDULE_SESSION) {
            setScheduleModalOpen(true);
          } else if (CTAClicked === CTAType.REQUEST_COACH_MATCH) {
            setRequestCoachMatchModalOpen(true);
          }
          await refetchCoachData();
        }}
        refetchQueries={[CoachMessageCtaGetUserDocument]}
      />
      <PhoneNumberModal />

      {coach ? (
        <MessageModal
          coach={coach}
          categoryUrn={categoryUrn}
          onSuccess={onSuccessfulMessagedCoach}
          onScheduleIntroCall={handleScheduleSession}
        />
      ) : null}

      {currentUser?.applicant && goal && categoryUrn ? (
        <RequestCoachMatchModal
          goal={goal}
          coach={coach}
          applicantUrn={currentUser.applicant.id}
          categoryUrn={categoryUrn}
        />
      ) : null}
    </div>
  );
};

export default CoachMessageCTA;
